/**
 * Zod schemas for the event permalink page (/event/{id}).
 *
 * Mirrors the Pydantic response models from fleet-api Plan 03-01 Task 3:
 *   GET /v1/alerts/{alert_id}         -> AlertDetail
 *   GET /v1/alerts/{alert_id}/related -> AlertRelated[]
 *
 * AlertDetail is a superset of AlertSummary (from the /now page) adding
 * the full evidence payload: RSSI waveform, camera clip URL, metadata bag.
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// RSSI waveform data point
// ---------------------------------------------------------------------------

/**
 * A single RSSI sample on the event waveform.
 *
 * t_offset_ms is relative to the alert fire timestamp (0 = alert fired).
 * Negative values are samples before the alert; positive are after.
 *
 * Phase 3 note: the cloud does not yet mirror per-detection RSSI.
 * The endpoint returns [] for rssi_waveform. The component renders a
 * placeholder message when the array is empty.
 */
export const RssiSampleSchema = z.object({
  /** Offset from alert fire time in milliseconds. Negative = before fire. */
  t_offset_ms: z.number().int(),
  /** RSSI value in dBm at this time offset. */
  rssi_dbm: z.number().int(),
});

export type RssiSample = z.infer<typeof RssiSampleSchema>;

// ---------------------------------------------------------------------------
// AlertDetail
// ---------------------------------------------------------------------------

/**
 * Alert outcome values (shared with AlertSummary from the /now page).
 */
export const AlertOutcomeSchema = z.enum(['turned_away', 'entered', 'loitering', 'other']);
export type AlertOutcome = z.infer<typeof AlertOutcomeSchema>;

/**
 * Full alert detail returned by GET /v1/alerts/{alert_id}.
 *
 * Extends the AlertSummary shape from /now with full evidence:
 * - rssi_waveform: time-series RSSI samples (empty [] in Phase 3)
 * - camera_clip_url: presigned URL to a video clip (null if no camera)
 * - evidence_url: generic evidence URL (fallback)
 * - metadata: arbitrary JSON bag from the detector
 * - device_type: inferred device platform
 * - confidence: fingerprint match confidence [0, 1]
 */
export const AlertDetailSchema = z.object({
  /** Alert primary key. */
  id: z.number().int(),
  /** Owning site UUID (org-scoped). */
  site_id: z.string().uuid(),
  /** Zone numeric ID; null if alert is site-level. */
  zone_id: z.number().int().nullable(),
  /** Zone display name; null if zone deleted or site-level. */
  zone_name: z.string().nullable(),
  /** Device fingerprint hash; null if unknown. */
  fingerprint: z.string().nullable(),
  /** Raw alert type string from detector. */
  alert_type: z.string(),
  /** Severity classification. */
  severity: z.enum(['info', 'warning', 'critical']),
  /** ISO 8601 timestamp when alert was opened (alert fire time, t=0 on waveform). */
  ts: z.string().datetime(),
  /*
   * Contract reconciliation (2026-06-01): the fleet-api AlertDetail
   * (fleet_api/alerts/schemas.py, built from alerts_mirror) does NOT send
   * resolved_at / outcome / has_camera_clip / thumbnail_url / metadata /
   * camera_clip_url / rssi_waveform. These were required keys in the
   * original NOW-era contract, so a plain .nullable() (which still REQUIRES
   * the key) made AlertDetailSchema.parse() throw a ZodError on every
   * fleet-api alert -> the /event page rendered "Something went wrong" for
   * every click. They are now .optional().default(...) so an absent key
   * resolves to a safe value (no component change needed; the canonical
   * RSSI + clip evidence lives on the /alerts/:id/replay bundle page).
   */
  /** ISO 8601 timestamp when alert was resolved; null if still active. */
  resolved_at: z.string().datetime().nullable().optional().default(null),
  /** Resolved outcome; null if unresolved or ambiguous. */
  outcome: AlertOutcomeSchema.nullable().optional().default(null),
  /** Whether a camera clip is available for this event. */
  has_camera_clip: z.boolean().optional().default(false),
  /** Presigned thumbnail URL; null if no clip or not yet rendered. */
  thumbnail_url: z.string().nullable().optional().default(null),
  /** Hardware chip ID of the triggering sensor; null if unknown. */
  chip_id: z.string().nullable(),
  /** Human-readable alert summary text; null if none. */
  message: z.string().nullable(),
  /** Arbitrary JSON metadata bag from the detector; null if empty. */
  metadata: z.record(z.unknown()).nullable().optional().default(null),
  /**
   * fleet-api names the metadata bag `metadata_json`; accept it as a
   * passthrough so the transform below can backfill `metadata` from it.
   */
  metadata_json: z.record(z.unknown()).nullable().optional(),
  /** Generic evidence URL (e.g. a snapshot); null if none. */
  evidence_url: z.string().nullable().optional().default(null),
  /** Presigned video clip URL from DigitalOcean Spaces; null if no clip. */
  camera_clip_url: z.string().nullable().optional().default(null),
  /**
   * RSSI waveform samples: t_offset_ms relative to ts (alert fire).
   * The cloud does not mirror per-detection RSSI on this endpoint, so this
   * is null/absent in the fleet contract. The RssiWaveform component renders
   * a placeholder when empty/null.
   */
  rssi_waveform: z.array(RssiSampleSchema).nullable().optional().default(null),
  /** Inferred device type (apple, android, unknown, etc.); null if unknown. */
  device_type: z.string().nullable().optional(),
  /** Fingerprint match confidence as a fraction [0, 1]; null if unknown. */
  confidence: z.number().min(0).max(1).nullable().optional(),
  /** ISO 8601 timestamp of first detection for this fingerprint; null if unknown. */
  first_seen: z.string().datetime().nullable().optional(),
  /** ISO 8601 timestamp of most recent detection for this fingerprint; null if unknown. */
  last_seen: z.string().datetime().nullable().optional(),
})
  // Backfill `metadata` from the fleet-api `metadata_json` field name so the
  // MetadataSidebar still shows the detector blob without a component change.
  .transform((d) => ({
    ...d,
    metadata: d.metadata ?? d.metadata_json ?? null,
  }));

export type AlertDetail = z.infer<typeof AlertDetailSchema>;

// ---------------------------------------------------------------------------
// AlertRelated
// ---------------------------------------------------------------------------

/**
 * One related event returned by GET /v1/alerts/{alert_id}/related.
 *
 * Related events share either the same fingerprint (past visits) or the
 * same zone within the same hour (same-zone same-hour context).
 *
 * The reason field distinguishes between the two correlation axes so the
 * RelatedEventsPanel can show the appropriate label.
 */
export const AlertRelatedSchema = z.object({
  /** Alert primary key. */
  id: z.number().int(),
  /** Owning site UUID. */
  site_id: z.string().uuid(),
  /** Zone numeric ID; null if site-level. */
  zone_id: z.number().int().nullable(),
  /** Zone display name; null if unknown. */
  zone_name: z.string().nullable(),
  /** Device fingerprint hash; null if unknown. */
  fingerprint: z.string().nullable(),
  /** Raw alert type. */
  alert_type: z.string(),
  /** Severity classification. */
  severity: z.enum(['info', 'warning', 'critical']),
  /** ISO 8601 alert open time. */
  ts: z.string().datetime(),
  /** ISO 8601 resolution time; null if still active. */
  resolved_at: z.string().datetime().nullable(),
  /** Resolved outcome; null if unresolved. */
  outcome: AlertOutcomeSchema.nullable(),
  /**
   * Why this event was returned as related.
   * 'same_fingerprint' - same device was seen before.
   * 'same_zone_same_hour' - different device in same zone during the same hour.
   */
  reason: z.enum(['same_fingerprint', 'same_zone_same_hour']).optional(),
});

export type AlertRelated = z.infer<typeof AlertRelatedSchema>;

// ---------------------------------------------------------------------------
// EntryEventDetail (Phase 12 Plan 12-11)
// ---------------------------------------------------------------------------

/**
 * Full entry-event detail returned by GET /v1/entry-events/{event_id}.
 *
 * Mirrors the fleet-api EntryEventDetailResponse shape exactly. The
 * EntryEventDrill component consumes this for the /event/entry-<id>
 * route added by Plan 12-11 Task 3.
 *
 * `rssi_waveform` is always [] in v1; `camera_clip_url` is always null.
 * Both placeholders lock the wire shape so a follow-up phase can
 * populate them without a contract bump.
 */
export const EntryEventDetailSchema = z.object({
  /** Cloud-side BigInt PK for the entry_events_mirror row. */
  id: z.number().int(),
  /** Parent Site UUID. */
  site_id: z.string().uuid(),
  /** Site slug (denormalized server-side from the joinedload). */
  site_slug: z.string().nullable(),
  /** Site display name (denormalized). */
  site_name: z.string().nullable(),
  /** Per-site remote PK on the on-site box; surfaced for ops debugging. */
  remote_event_id: z.number().int(),
  /** ISO 8601 event timestamp. */
  ts: z.string(),
  /** Sensor chip id; null when unidentifiable. */
  chip_id: z.string().nullable(),
  /** Zone numeric id. */
  zone_id: z.number().int().nullable(),
  /** Zone display name. */
  zone_name: z.string().nullable(),
  /** RSSI in dBm. */
  rssi: z.number().int().nullable(),
  /** Cloud-computed deterrence outcome (column read; may be null on legacy paths). */
  deterrence_outcome: z.enum(['deterred', 'escalated', 'unknown']).nullable(),
  /** Free-form metadata bag from the remote. */
  metadata_json: z.record(z.unknown()).nullable(),
  /** Placeholder for per-detection RSSI samples; [] in v1. */
  rssi_waveform: z.array(z.record(z.unknown())),
  /** Placeholder for an evidence clip URL; null in v1. */
  camera_clip_url: z.string().nullable(),
});

export type EntryEventDetail = z.infer<typeof EntryEventDetailSchema>;
